// App theme: light/dark mode + full per-color customization. The palette is a set of CSS custom
// properties on :root (defaults in App.css for dark, `:root[data-theme="light"]` for light). This module
// owns the SETTINGS side: which mode, and any user color overrides, applied by stamping data-theme + inline
// custom properties onto document.documentElement (inline props beat the stylesheet, so they override the
// chosen mode). Dark is the default and unchanged from the original theme.
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { settingsGet, settingsSet } from "./api";
import { bgUrl, dataUriToBytes, inlineBackground, isAnimatedWebp, processBackgroundImage, storeBackground } from "./bgImage";
import { invoke, isRemote } from "./rpc";

export type ThemeMode = "dark" | "light";

/** The three views that can carry a background image. */
export const BG_VIEWS = ["library", "worldbooks", "chats"] as const;
export type BgView = (typeof BG_VIEWS)[number];
export const DEFAULT_BG_DIM = 0.7; // scrim strength over a background image (0 = image raw, ~0.95 = nearly hidden)

/** The message-text colors a theme carries (they live in ChatStyle; a pack snapshots them). */
export interface ThemeChatColors {
  quoteColor: string;
  italicColor: string;
  boldColor: string;
  textColor: string;
  /** 0-100. Travels with the theme because it only makes sense next to that theme's background image. */
  bubbleOpacity?: number;
  /** 0-100, the library tiles' background and border, and their card pictures. With the background image too. */
  tileOpacity?: number;
  tileImageOpacity?: number;
}

/** One customizable color token: the CSS var, a human label, and which group it sits in (for the picker UI). */
export interface ThemeVar {
  name: string; // CSS custom property, e.g. "--bg"
  /** Lazy descriptor (this module loads before the UI language is active) — show it with i18n._(). */
  label: MessageDescriptor;
  /** The whole truth, shown on hover. The label has ~20 characters; most tokens paint more than that can
   *  say, and a label that names only half the job is how "the colors don't do what they say" starts. */
  hint: MessageDescriptor;
  group: "Surfaces" | "Ink" | "Accent" | "Status";
}

/** Every color the user can customize — must match the vars defined in App.css (dark + light). */
/* Labels say what a token PAINTS, not what color it ships as — users retheme, so "Purple" may not be
 * purple. Keep them under ~20 chars so the palette grid never truncates.
 *
 * These are DERIVED FROM App.css, by counting which selectors actually read each var, and they have to be
 * re-derived whenever a var's usage moves. Guessing produced a palette where, as a tester put it, "ALL
 * the colors are incorrect as to what they change": --surface-2 and --surface-accent had each other's
 * jobs (6 of 8 --surface-2 uses are :hover rows; --surface-accent is mostly badges, callouts and inset
 * blocks), --purple-dark was called "Selected items" while its most visible job is the user's own message
 * bubble, --bg-alt claimed "rails" without mentioning that it is also the composer, and
 * --color-nav-header — "Title bar" — was read by exactly one rule in the whole app: the little mock
 * inside this very picker. There is a test below that keeps every var here in use somewhere real. */
export const THEME_VARS: ThemeVar[] = [
  { name: "--bg", label: msg`Background`, group: "Surfaces",
    hint: msg`The page behind everything — plus modals, drawers, editors and the settings panes.` },
  { name: "--bg-alt", label: msg`Sidebars & inputs`, group: "Surfaces",
    hint: msg`Sidebars and rails, the message composer, and every text field and inset row.` },
  { name: "--surface", label: msg`Cards & replies`, group: "Surfaces",
    hint: msg`Cards, menus, toasts and list rows — and the character's reply bubble.` },
  { name: "--surface-accent", label: msg`Hover & badges`, group: "Surfaces",
    hint: msg`Most hover states, plus badges, callouts, chips and inset blocks.` },
  { name: "--surface-2", label: msg`Alt row hover`, group: "Surfaces",
    hint: msg`The neutral second tone: the card rail, lore rows and title history on hover, and the memory tree.` },
  { name: "--color-nav-header", label: msg`Title bar`, group: "Surfaces",
    hint: msg`The strip along the top of the window, behind the app's own navigation.` },
  { name: "--foreground", label: msg`Text`, group: "Ink",
    hint: msg`Body text everywhere, and the slider handles.` },
  { name: "--muted", label: msg`Muted text`, group: "Ink",
    hint: msg`Secondary text: hints, timestamps, placeholders, disabled labels, a fumbled roll.` },
  { name: "--border", label: msg`Borders`, group: "Ink",
    hint: msg`Every hairline: panel edges, dividers, field outlines, slider tracks.` },
  { name: "--border-accent", label: msg`Highlighted borders`, group: "Ink",
    hint: msg`The border on a focused, selected or otherwise active element.` },
  { name: "--purple", label: msg`Buttons & accents`, group: "Accent",
    hint: msg`Standard buttons, toggles, slider fills, the scrollbar, progress bars and blockquotes.` },
  { name: "--button-border", label: msg`Button borders`, group: "Accent",
    hint: msg`The outline around standard buttons, apart from the panels' Borders. Until you set it, it is a darker shade of Buttons & accents and follows it.` },
  { name: "--purple-light", label: msg`Links & hover text`, group: "Accent",
    hint: msg`Links, hovered text, focus rings, and the dot that pulses while a reply streams.` },
  { name: "--purple-dark", label: msg`Your messages`, group: "Accent",
    hint: msg`Your own message bubble in a chat.` },
  { name: "--selected-bg", label: msg`Selected items`, group: "Accent",
    hint: msg`The chosen option in toggles, tabs and chips, the open folder and settings page, and highlighted rows.` },
  { name: "--tab-bg", label: msg`Chat tabs`, group: "Accent",
    hint: msg`The chat tabs button, and the circle behind a tab that has no picture.` },
  { name: "--orange", label: msg`Action buttons`, group: "Accent",
    hint: msg`The Send button and other primary actions, and the accents that go with them.` },
  { name: "--orange-dark", label: msg`Action gradient`, group: "Accent",
    hint: msg`The second color of the gradient on Send and the other orange call-to-action buttons. Match it to Action buttons for a flat look.` },
  { name: "--green", label: msg`Success`, group: "Status",
    hint: msg`Success: ready dots, synced, a story running, a winning roll, a gain.` },
  { name: "--danger", label: msg`Danger`, group: "Status",
    hint: msg`Destructive buttons, delete and close hovers, damage, losses and over-budget context.` },
  { name: "--warning", label: msg`Warning`, group: "Status",
    hint: msg`Caution: warning badges, callout edges and a roll waiting on an outcome.` },
];

/** Baseline values per mode — the picker seeds from these when the user hasn't overridden a color, and
 *  "reset" returns to them. MUST stay in sync with App.css (:root and :root[data-theme="light"]). */
export const THEME_PRESETS: Record<ThemeMode, Record<string, string>> = {
  dark: {
    "--bg": "#1e1e1e",
    "--bg-alt": "#181818",
    "--surface": "#252525",
    "--surface-accent": "#171427",
    "--surface-2": "#2c2c2c",
    "--color-nav-header": "#181818",
    "--foreground": "#e2e8f0",
    "--muted": "#b7b3d6",
    "--border": "#333333",
    "--border-accent": "#3f2e7a",
    "--purple": "#6b58a6",
    "--button-border": "#4b3e74", // --accent-deep: --purple 70% toward black
    "--purple-light": "#9b86ff",
    "--purple-dark": "#4b3e72",
    "--selected-bg": "#4b3e72",
    "--tab-bg": "#4b3e72",
    "--orange": "#f37a21",
    "--orange-dark": "#e4711f",
    "--green": "#58c27d",
    "--danger": "#e05555",
    "--warning": "#e0a030",
  },
  light: {
    "--bg": "#f4f5f8",
    "--bg-alt": "#eaecf1",
    "--surface": "#ffffff",
    "--surface-accent": "#efeafc",
    "--surface-2": "#e7e9ef",
    "--color-nav-header": "#eaecf1",
    "--foreground": "#1f2230",
    "--muted": "#5c5876",
    "--border": "#babfcc",
    "--border-accent": "#c3b6ee",
    "--purple": "#6a54c8",
    "--button-border": "#6a54c8", // light --accent-deep is --purple itself
    "--purple-light": "#6a45d9",
    "--purple-dark": "#6a54c8",
    "--selected-bg": "#e2d9f7",
    "--tab-bg": "#6a54c8",
    "--orange": "#b05506",
    "--orange-dark": "#964804",
    "--green": "#1f7a46",
    "--danger": "#c23434",
    "--warning": "#855607",
  },
};

export interface ThemeSettings {
  mode: ThemeMode;
  /** Per-var overrides (CSS var → hex). Sparse; unset vars fall back to the mode preset. */
  custom: Record<string, string>;
  /** Per-view background images (WebP data URIs). */
  backgrounds?: Partial<Record<BgView, string>>;
  /** Scrim strength over background images (0..~0.95). Default DEFAULT_BG_DIM. */
  bgDim?: number;
  /** Lit edge plus a halo in their own color on the accent and action buttons (`:root.theme-glow` in App.css). */
  glow?: boolean;
  /** Already past the 0.7.6 accent split (splitAccent), so it never runs on this again. */
  accentSplit?: true;
}

export const DEFAULT_THEME: ThemeSettings = { mode: "dark", custom: {} };

/** A shareable, nameable theme: palette + backgrounds + message colors. Saved as a named preset and the
 *  payload of a .rctheme archive. Message colors live in ChatStyle; a pack snapshots/restores them. */
export interface ThemePack {
  name: string;
  mode: ThemeMode;
  custom: Record<string, string>;
  backgrounds?: Partial<Record<BgView, string>>;
  bgDim?: number;
  glow?: boolean;
  /** As on ThemeSettings. */
  accentSplit?: true;
  chat: ThemeChatColors;
}

/**
 * Announced whenever the palette is saved, so EVERY window repaints — not just the one that was edited.
 *
 * Settings can be popped out into its own OS window, and `applyTheme` only ever stamps the document it
 * runs in. Without this, colors changed in the pop-out landed on the pop-out alone and the app behind it
 * kept the old palette until a restart ("pop-out doesn't apply to the app").
 */
export const THEME_CHANGED_EVENT = "aicc:theme-changed";

const KEY = "ui_theme";
const PRESETS_KEY = "ui_theme_presets";

/** #rrggbb → "r, g, b" (falls back to a neutral gray on a bad value). */
function hexToRgb(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return "30, 30, 30";
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

/**
 * Text that stays readable on a themed fill.
 *
 * Buttons, chips and your own message bubble printed literal `#fff` on top of `--purple`, `--danger` and
 * friends. That is correct for the shipped colors and silently unreadable the moment someone themes one
 * of them pale — white on pale yellow is not a broken swatch, but it is a broken theme.
 *
 * `applyTheme` computes a companion `--on-<token>` for each fill and the stylesheet prints THAT instead.
 *
 * White is kept unless it drops below 3:1 — WCAG's floor for large/bold text, which is what these labels
 * are. Not "whichever contrasts more": that test would put dark text on the red delete button (dark scores
 * 5.0 there against white's 3.6), which is technically better and visibly wrong. 3:1 is a real line rather
 * than a number picked to avoid changing anything, and it turned out two shipped pairings were already
 * under it — white on --orange is 2.76:1, which is why the theme preview had always drawn the Send button
 * with dark ink while the real button used white.
 */
const DARK_INK = "#12101a";
// 4.5 = the WCAG AA minimum for normal-size text, which button labels are. At the old value of 3,
// white "won" on any fill it merely reached 3:1 against — the light theme's orange CTAs shipped with
// 3.2:1 white labels, and the dark theme's danger buttons with 3.8:1. White is preferred only when it
// actually passes; otherwise dark ink (which by construction clears the bar wherever white doesn't).
const INK_FLIP_BELOW = 4.5;

/** WCAG 2.1 relative luminance of #rrggbb. */
function luminance(hex: string): number {
  const t = hex.trim();
  // Imported themes can carry #rgb or rgb()/rgba(); a value this can't read is treated as mid-grey
  // (luminance 0.2) rather than black, so an unreadable fill doesn't silently earn white ink.
  let m = /^#?([0-9a-f]{6})$/i.exec(t);
  if (!m) {
    const short = /^#?([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(t);
    if (short) m = [t, short[1] + short[1] + short[2] + short[2] + short[3] + short[3]] as unknown as RegExpExecArray;
  }
  if (!m) {
    const rgb = /^rgba?\(\s*(\d{1,3})\s*[, ]\s*(\d{1,3})\s*[, ]\s*(\d{1,3})/i.exec(t);
    if (rgb) m = [t, [rgb[1], rgb[2], rgb[3]].map((c) => Math.min(255, +c).toString(16).padStart(2, "0")).join("")] as unknown as RegExpExecArray;
  }
  if (!m) return 0.2;
  const n = parseInt(m[1], 16);
  const chan = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * chan((n >> 16) & 255) + 0.7152 * chan((n >> 8) & 255) + 0.0722 * chan(n & 255);
}

/** WCAG contrast ratio between two hex colors: 1 (identical) to 21 (black on white). */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** The ink to print ON `fill`. */
export function inkOn(fill: string): string {
  return contrastRatio("#ffffff", fill) >= INK_FLIP_BELOW ? "#ffffff" : DARK_INK;
}

/** Fills that carry text of their own, and therefore need an `--on-*` companion. */
const INK_FILLS = ["--purple", "--purple-dark", "--selected-bg", "--tab-bg", "--purple-light", "--orange", "--danger"] as const;

/**
 * "Your messages", "Selected items" and "Chat tabs" were one color, --purple-dark, until 0.7.6. A theme
 * saved before the split carries its choice only under --purple-dark, so it is copied to the two new
 * tokens where they used to follow it: the tabs in both modes, and selections in dark mode (light mode
 * always had its own pale selection tint). The look of an existing theme does not change.
 */
function splitAccent(mode: ThemeMode, custom: Record<string, string>, done: boolean): Record<string, string> {
  // Once only: run on every load, it put a Chat tabs color the user had reset straight back.
  const old = custom["--purple-dark"];
  if (!old || done) return custom;
  const out = { ...custom };
  out["--tab-bg"] ??= old;
  if (mode === "dark") out["--selected-bg"] ??= old;
  return out;
}

export async function loadTheme(): Promise<ThemeSettings> {
  const raw = await settingsGet(KEY);
  // No saved theme yet (fresh account) → honor the mode the user picked in onboarding, cached unencrypted.
  if (!raw) {
    let mode: ThemeMode = "dark";
    try { if (localStorage.getItem(THEME_MODE_CACHE) === "light") mode = "light"; } catch { /* ignore */ }
    return { mode, custom: {} };
  }
  try {
    const v = JSON.parse(raw) as Partial<ThemeSettings>;
    return {
      mode: v.mode === "light" ? "light" : "dark",
      custom: splitAccent(v.mode === "light" ? "light" : "dark", (v.custom && typeof v.custom === "object" ? v.custom : {}) as Record<string, string>, v.accentSplit === true),
      accentSplit: true,
      // Restore the background images + dim too — dropping these here is what made a themed background vanish
      // (and then get auto-saved away) on every login.
      backgrounds: v.backgrounds && typeof v.backgrounds === "object" ? v.backgrounds : undefined,
      bgDim: typeof v.bgDim === "number" ? v.bgDim : undefined,
      glow: v.glow === true || undefined,
    };
  } catch {
    return DEFAULT_THEME;
  }
}

export async function saveTheme(t: ThemeSettings): Promise<void> {
  await settingsSet(KEY, JSON.stringify(t));
}

/** A background bigger than this was never produced by the picker (1920px WebP is a few hundred KB). */
const BG_REPAIR_OVER_BYTES = 1_500_000;

type WithBackgrounds = { backgrounds?: Partial<Record<BgView, string>> };

/**
 * Move every background still held inline (a data: URI) into the image store, leaving its reference
 * (bgImage.storeBackground). Returns the SAME object when there was nothing to move, so callers can tell.
 *
 * `shrink` first re-encodes an oversized still: `.rctheme` import once stored them at full size, up to
 * 12 MB per view. An animated one is past that size legitimately and was re-encoded under its own cap on
 * the way in; re-encoding it again would only lose quality. Best-effort per image: one that will not
 * store stays inline, which still works.
 */
export async function storeBackgrounds<T extends WithBackgrounds>(x: T, shrink = false): Promise<T> {
  const bg = x.backgrounds;
  if (!bg) return x;
  const out = { ...bg };
  let changed = false;
  for (const view of BG_VIEWS) {
    let uri = bg[view];
    if (typeof uri !== "string" || !uri.startsWith("data:")) continue;
    try {
      if (shrink && uri.length > BG_REPAIR_OVER_BYTES) {
        const bytes = dataUriToBytes(uri);
        if (!isAnimatedWebp(bytes)) {
          // Must claim image/* or processImage's gate throws; the codec is sniffed, not trusted.
          const p = await processBackgroundImage(new Blob([bytes as BlobPart], { type: "image/png" }));
          if (p.dataUri.length < uri.length) uri = p.dataUri;
        }
      }
      out[view] = await storeBackground(uri);
      changed = true;
    } catch { /* keep it inline; a background is not worth losing */ }
  }
  return changed ? { ...x, backgrounds: out } : x;
}

/** The pack with its backgrounds as data URIs again, for a .rctheme export. */
export async function inlineBackgrounds(p: ThemePack): Promise<ThemePack> {
  if (!p.backgrounds) return p;
  const out: Partial<Record<BgView, string>> = {};
  for (const [view, v] of Object.entries(p.backgrounds) as Array<[BgView, string]>) out[view] = await inlineBackground(v);
  return { ...p, backgrounds: out };
}

/**
 * At unlock: move inline backgrounds out of the applied theme and the saved themes, then delete stored
 * ones nothing names any more. Themes saved before the image store held their images inline, which put
 * megabytes into a settings row read on every unlock. Desktop only: the sweep deletes files.
 */
export async function migrateThemeBackgrounds(t: ThemeSettings): Promise<void> {
  if (isRemote()) return;
  // Storing takes seconds; the user may change the theme meanwhile. So what is written back is the theme as
  // it is THEN, with only the backgrounds that are still the same inline image swapped for their refs.
  const swapped = new Map<string, string>();
  const note = <T extends WithBackgrounds>(before: T, after: T) => {
    for (const v of BG_VIEWS) {
      const a = before.backgrounds?.[v], b = after.backgrounds?.[v];
      if (a && b && a !== b) swapped.set(a, b);
    }
  };
  const swap = <T extends WithBackgrounds>(x: T): T => {
    if (!x.backgrounds) return x;
    let changed = false;
    const bg = { ...x.backgrounds };
    for (const v of BG_VIEWS) {
      const ref = bg[v] && swapped.get(bg[v]!);
      if (ref) { bg[v] = ref; changed = true; }
    }
    return changed ? { ...x, backgrounds: bg } : x;
  };
  note(t, await storeBackgrounds(t, true));
  for (const p of await loadThemePresets()) note(p, await storeBackgrounds(p, true));
  if (swapped.size) {
    const now = await loadTheme();
    const theme = swap(now);
    if (theme !== now) await saveTheme(theme);
    const presets = await loadThemePresets();
    const moved = presets.map(swap);
    if (moved.some((p, i) => p !== presets[i])) await saveThemePresets(moved);
  }
  await invoke<number>("theme_bg_sweep");
}

/** Apply a theme to the live document: set data-theme (picks the stylesheet block) and stamp any color
 *  overrides as inline custom properties (which beat the stylesheet). Clears every managed var first so
 *  removing an override reverts to the mode preset. Safe to call repeatedly. */
export function applyTheme(t: ThemeSettings): void {
  const root = document.documentElement;
  root.dataset.theme = t.mode;
  root.style.colorScheme = t.mode;
  for (const v of THEME_VARS) {
    const override = t.custom[v.name];
    if (override) root.style.setProperty(v.name, override);
    else root.style.removeProperty(v.name);
  }
  // Background images: set a CSS var + a gating class per view. The scrim tint is derived from the (possibly
  // customized) base background so it reads correctly in both modes; App.css layers scrim over image.
  // Readable ink for every fill that carries text. Derived, never stored — a theme has no say in this
  // beyond the fill it chose.
  for (const f of INK_FILLS) {
    root.style.setProperty(`--on${f.slice(1)}`, inkOn(t.custom[f] ?? THEME_PRESETS[t.mode][f] ?? "#000000"));
  }
  root.classList.toggle("theme-glow", !!t.glow);
  const dim = Math.min(0.95, Math.max(0, t.bgDim ?? DEFAULT_BG_DIM));
  const baseBg = t.custom["--bg"] ?? THEME_PRESETS[t.mode]["--bg"];
  root.style.setProperty("--bg-scrim", `rgba(${hexToRgb(baseBg)}, ${dim})`);
  const bg = t.backgrounds ?? {};
  for (const view of BG_VIEWS) {
    const uri = bg[view];
    if (uri) {
      root.style.setProperty(`--bg-image-${view}`, `url("${bgUrl(uri)}")`);
      root.classList.add(`has-bg-${view}`);
    } else {
      root.style.removeProperty(`--bg-image-${view}`);
      root.classList.remove(`has-bg-${view}`);
    }
  }
  // Cache the mode unencrypted so the pre-unlock boot (main.tsx) can pick it up with no flash of the wrong
  // theme. The real settings (incl. custom colors) load per-account after unlock.
  try { localStorage.setItem(THEME_MODE_CACHE, t.mode); } catch { /* ignore */ }
}

// --- named theme packs (save / share) --------------------------------------------------------------

/** Snapshot a live theme + its message colors into a nameable pack. */
export function toPack(name: string, t: ThemeSettings, chat: ThemeChatColors): ThemePack {
  return {
    name: name.trim() || "Theme",
    mode: t.mode,
    custom: { ...t.custom },
    backgrounds: t.backgrounds ? { ...t.backgrounds } : undefined,
    bgDim: t.bgDim,
    glow: t.glow,
    accentSplit: true,
    chat: { ...chat },
  };
}

/** The ThemeSettings half of a pack (palette + backgrounds). Chat colors are applied separately via ChatStyle. */
export function packToTheme(p: ThemePack): ThemeSettings {
  return { mode: p.mode, custom: splitAccent(p.mode, p.custom, p.accentSplit === true), backgrounds: p.backgrounds ? { ...p.backgrounds } : undefined, bgDim: p.bgDim, glow: p.glow, accentSplit: true };
}

export async function loadThemePresets(): Promise<ThemePack[]> {
  const raw = await settingsGet(PRESETS_KEY);
  if (!raw) return [];
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) ? (v as ThemePack[]) : [];
  } catch {
    return [];
  }
}

export async function saveThemePresets(list: ThemePack[]): Promise<void> {
  await settingsSet(PRESETS_KEY, JSON.stringify(list));
}

const ACTIVE_KEY = "ui_theme_active";
/** The name of the saved theme currently applied (so the picker restores it across restarts). "" = none. */
export async function loadActiveThemeName(): Promise<string> {
  return (await settingsGet(ACTIVE_KEY)) ?? "";
}
export async function saveActiveThemeName(name: string): Promise<void> {
  await settingsSet(ACTIVE_KEY, name);
}

/** localStorage key for the fast pre-unlock mode hint (see applyTheme + applyCachedThemeMode). */
export const THEME_MODE_CACHE = "ui_theme_mode";

/** Apply just the cached mode synchronously at startup (before React/DB), to avoid a theme flash. */
export function applyCachedThemeMode(): void {
  try {
    const m = localStorage.getItem(THEME_MODE_CACHE);
    const mode: ThemeMode = m === "light" ? "light" : "dark";
    document.documentElement.dataset.theme = mode;
    document.documentElement.style.colorScheme = mode;
  } catch { /* ignore */ }
}

/** The effective value shown in a picker: the user's override, else the current mode's preset. */
export function effectiveColor(t: ThemeSettings, name: string): string {
  return t.custom[name] ?? THEME_PRESETS[t.mode][name] ?? "#000000";
}
